import React from 'react';
import { ReactComponent as ReturnPercentage } from 'assets/icons/project-return-money-green.svg';
import { ReactComponent as Frequency } from 'assets/icons/certification-green.svg';
import { ReactComponent as Money } from 'assets/icons/project-money-green.svg';
import { ReactComponent as BarGraph } from 'assets/icons/barGraph.svg';
import { ReactComponent as Time } from 'assets/icons/project-watch-money-green.svg';
import { ReactComponent as Document } from 'assets/icons/document-money-green.svg';
import { formatNumber } from 'util/formatNumber';
import { colorByProjectType, typesProjectType } from 'types/projectType';
import { calculatePercentage } from 'util/percent';
import { capitalizeFirstLetterString } from 'util/capitalizeFirstLetter';
import { useMediaQuery } from 'hooks';
import { payFrequency } from 'types/payFrecuency';
import { Grid, Box } from '@material-ui/core';
import InfoCard, { InfoCardTemplate } from 'components/InfoCard';
import { colors } from 'globalStyle';
import { Title, Text } from 'components/Texts';
import { routes } from 'routes';
import { Ghost } from 'components/Buttons';

export default function EquityConditions({
  styles,
  ieaTax,
  maxAmount,
  frequency,
  minAmount,
  projectType,
  annualValuationPercentage,
  percentageProvisions,
  currencyIso,
  adminTax,
  investmentTimeDescription,
  valuation,
  resourcesGathering,
  projectId,
  id,
  color,
}) {
  const isDesktop = useMediaQuery(1023);
  const conditionsEquityProject = [
    {
      xs: isDesktop && 4,
      image: (
        <ReturnPercentage color={color || colorByProjectType[projectType]} />
      ),
      style: styles.primaryCard,
      title: `Recibe el ${ieaTax * 100}% E.A. aproximado`,
      subtitle: 'Retorno estimado',
      imageWidth: isDesktop ? '50px' : '30px',
      description:
        'Es el porcentaje esperado que recibes en un año sobre tu inversión inicial. Esta rentabilidad es variable.',
    },
    {
      xs: isDesktop && 4,
      image: <BarGraph color={color || colorByProjectType[projectType]} />,
      style: styles.primaryCard,
      title: `${calculatePercentage(annualValuationPercentage)}%`,
      subtitle: 'Valoración anual estimada',
      imageWidth: isDesktop ? '45px' : '28px',
      description:
        'Posible incremento sobre el valor comercial actual del activo en un año.',
    },
    {
      xs: isDesktop && 4,
      image: <Money color={color || colorByProjectType[projectType]} />,
      style: styles.primaryCard,
      title: formatNumber(valuation, null, currencyIso),
      subtitle: 'Valor del activo',
      imageWidth: isDesktop ? '50px' : '30px',
      description:
        'Es el precio comercial del activo según su valorización actual.',
    },
    {
      xs: isDesktop && 4,
      image: <Money color={color || colorByProjectType[projectType]} />,
      style: isDesktop ? styles.thirdCard : styles.primaryCard,
      title: formatNumber(minAmount, null, currencyIso),
      subtitle: 'Recaudo mínimo',
      imageWidth: isDesktop ? '50px' : '30px',
      description:
        'La meta de financiamiento inicial para iniciar el proyecto.',
      hoverDescription: true,
      colorByProjectType: colorByProjectType[projectType],
    },
    {
      xs: isDesktop && 4,
      image: <Money color={color || colorByProjectType[projectType]} />,
      style: isDesktop ? styles.thirdCard : styles.primaryCard,
      title: `${formatNumber(maxAmount, null, currencyIso)}`,
      subtitle: 'Recaudo máximo',
      imageWidth: isDesktop ? '50px' : '30px',
      description:
        'La cantidad total de dinero que la empresa busca recaudar para financiar su proyecto.',
      hoverDescription: true,
      colorByProjectType: colorByProjectType[projectType],
    },
    {
      isBigTitle: true,
      xs: isDesktop && 4,
      image: <Document color={color || colorByProjectType[projectType]} />,
      style: isDesktop ? styles.thirdCard : styles.primaryCard,
      title: (
        <>
          <span style={{ textAlign: 'center' }}>{payFrequency[frequency]}</span>
          <br />
          Recuerda que al ser una Renta Variable puede ser que no recibas pagos
          en todos los períodos.
        </>
      ),
      subtitle: 'Período de pago de rendimientos:',
      imageWidth: isDesktop ? '45px' : '28px',
      description:
        'Indica la periodicidad de pago de rendimientos variables sobre la inversión realizada. (mensual, bimestral, trimestral, o semestral)',
      hoverDescription: true,
      colorByProjectType: colorByProjectType[projectType],
    },
    {
      xs: isDesktop && 3,
      image: <Time color={color || colorByProjectType[projectType]} />,
      style: isDesktop ? styles.secondaryCard : styles.primaryCard,
      title: resourcesGathering,
      subtitle: 'Recolección de recursos',
      imageWidth: isDesktop ? '50px' : '30px',
      description:
        'Tiempo durante el cual se planea hacer la recolección de los recursos para la financiación.',
      hoverDescription: true,
      colorByProjectType: colorByProjectType[projectType],
    },
    {
      xs: isDesktop && 3,
      image: <Frequency color={color || colorByProjectType[projectType]} />,
      style: isDesktop ? styles.thirdCard : styles.primaryCard,
      title: `${calculatePercentage(percentageProvisions)}%`,
      subtitle: 'Provisiones',
      imageWidth: isDesktop ? '45px' : '28px',
      description:
        'Porcentaje periódico resguardado para solventar imprevistos y obligaciones tributarias',
      hoverDescription: true,
      colorByProjectType: colorByProjectType[projectType],
    },
    {
      xs: isDesktop && 3,
      image: <Frequency color={color || colorByProjectType[projectType]} />,
      style: isDesktop ? styles.thirdCard : styles.primaryCard,
      title: `${calculatePercentage(adminTax)}%`,
      subtitle: 'Cuota de administración',
      imageWidth: isDesktop ? '45px' : '28px',
      description:
        'Porcentaje cobrado por el administrador del activo sobre la renta periodica.',
      hoverDescription: true,
      colorByProjectType: colorByProjectType[projectType],
    },
    {
      xs: isDesktop && 3,
      image: <Time color={color || colorByProjectType[projectType]} />,
      style: isDesktop ? styles.thirdCard : styles.primaryCard,
      title: `${capitalizeFirstLetterString(investmentTimeDescription)}`,
      subtitle: 'Tiempo de la inversión',
      imageWidth: isDesktop ? '50px' : '30px',
      description: 'El período de tiempo previsto donde el proyecto genera rentabilidad',
      hoverDescription: true,
      colorByProjectType: colorByProjectType[projectType],
    },
  ];

  if (!isDesktop) {
    return (
      <Grid container spacing={isDesktop && 2} className={styles.root}>
        {conditionsEquityProject?.map(
          ({
            xs,
            image,
            style,
            title,
            subtitle,
            imageWidth,
            isBigTitle,
            description,
            hoverDescription,
          }) => (
            <Grid item xs={xs}>
              <InfoCard
                isDesktop={isDesktop}
                image={image}
                className={style}
                color={colors.GRAY_10}
                title={title}
                subtitle={subtitle}
                imageWidth={imageWidth}
                description={description}
                fullDescription
                centerDescription
                isBigTitle={isBigTitle}
                hoverDescription={hoverDescription && isDesktop}
                colorByProjectType={color || colorByProjectType[projectType]}
              />
            </Grid>
          ),
        )}
        <Grid item xs={isDesktop && 4}>
          <InfoCardTemplate className={styles.primaryCard}>
            <Grid
              container
              justify="space-evenly"
              direction="column"
              alignItems="center"
              style={{ height: '100%' }}
            >
              <Box width="45px">
                <Time color={color || colorByProjectType[projectType]} />
              </Box>
              <Text size={12} isCenter>
                Tipo de proyecto:
              </Text>
              <Title size={12} isCenter>
                {typesProjectType[projectType]}
              </Title>
              <Ghost
                underline
                size={12}
                color={colors.primary}
                href={`${routes.conditionsAndCharacteristics.route}/${projectType.toLowerCase()}/${id || projectId}`}
              >
                Conoce más
              </Ghost>
            </Grid>
          </InfoCardTemplate>
        </Grid>

      </Grid>
    );
  }

  return (
    <Grid container spacing={isDesktop && 2} className={styles.root}>
      {conditionsEquityProject?.map(
        ({
          xs,
          image,
          style,
          title,
          subtitle,
          imageWidth,
          isBigTitle,
          description,
          hoverDescription,
        }) => (
          <Grid item xs={xs}>
            <InfoCard
              isDesktop={isDesktop}
              image={image}
              className={style}
              color={colors.GRAY_10}
              title={title}
              subtitle={subtitle}
              imageWidth={imageWidth}
              description={description}
              fullDescription
              centerDescription
              isBigTitle={isBigTitle}
              hoverDescription={hoverDescription && isDesktop}
              colorByProjectType={color || colorByProjectType[projectType]}
            />
          </Grid>
        ),
      )}
    </Grid>
  );
}
